import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../src/contexts/AuthContext';
import { supabase } from '../../src/lib/supabase';
import { logUsageEvent } from '../../src/lib/logger';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import { Parceiro } from '../../types';
import {
  Handshake,
  Search,
  X,
  MapPin,
  Phone,
  Building2,
  Calendar,
  Users,
  Clock,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Briefcase,
  Filter,
  ChevronDown,
  UserPlus,
} from 'lucide-react';
import CreateUserModal from '../../src/components/CreateUserModal';

interface ParceirosModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const STATUS_CONFIG: Record<string, { label: string; emoji: string; dotColor: string; pillBg: string; pillText: string; pillBorder: string }> = {
  novo: {
    label: 'Novo',
    emoji: '🔵',
    dotColor: 'bg-blue-500',
    pillBg: 'bg-blue-50 dark:bg-blue-900/20',
    pillText: 'text-blue-700 dark:text-blue-300',
    pillBorder: 'border-blue-200 dark:border-blue-700',
  },
  em_analise: {
    label: 'Em Análise',
    emoji: '🟡',
    dotColor: 'bg-amber-500',
    pillBg: 'bg-amber-50 dark:bg-amber-900/20',
    pillText: 'text-amber-700 dark:text-amber-300',
    pillBorder: 'border-amber-200 dark:border-amber-700',
  },
  aprovado: {
    label: 'Aprovado',
    emoji: '🟢',
    dotColor: 'bg-emerald-500',
    pillBg: 'bg-emerald-50 dark:bg-emerald-900/20',
    pillText: 'text-emerald-700 dark:text-emerald-300',
    pillBorder: 'border-emerald-200 dark:border-emerald-700',
  },
  rejeitado: {
    label: 'Rejeitado',
    emoji: '🔴',
    dotColor: 'bg-red-500',
    pillBg: 'bg-red-50 dark:bg-red-900/20',
    pillText: 'text-red-700 dark:text-red-300',
    pillBorder: 'border-red-200 dark:border-red-700',
  },
};

// Custom Status Pill with fixed-position popover (avoids overflow clipping)
const StatusPill: React.FC<{ status: string; onChangeStatus: (s: string) => void; disabled?: boolean }> = ({ status, onChangeStatus, disabled }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleToggle = () => {
    if (disabled) return;
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 6, left: rect.right - 176 });
    }
    setOpen(!open);
  };

  const conf = STATUS_CONFIG[status] || STATUS_CONFIG.novo;

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleToggle}
        disabled={disabled}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all duration-200 ${conf.pillBg} ${conf.pillText} ${conf.pillBorder} ${disabled ? 'opacity-50 cursor-wait' : 'hover:shadow-md cursor-pointer'}`}
      >
        <span className={`w-2 h-2 rounded-full ${conf.dotColor} flex-shrink-0`} />
        {conf.label}
        <ChevronDown size={12} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[150]" onClick={() => setOpen(false)} />
          <div
            ref={menuRef}
            style={{ position: 'fixed', top: menuPos.top, left: menuPos.left }}
            className="w-44 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 p-1 z-[151] animate-fade-in"
          >
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <button
                key={key}
                onClick={() => {
                  onChangeStatus(key);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all duration-150 ${status === key
                  ? 'bg-gray-100 dark:bg-gray-700 font-bold'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 font-medium'
                  } text-gray-700 dark:text-gray-200`}
              >
                <span className={`w-2.5 h-2.5 rounded-full ${cfg.dotColor} flex-shrink-0`} />
                <span className="flex-1 text-left">{cfg.label}</span>
                {status === key && (
                  <CheckCircle2 size={14} className="text-[#5F6C37] flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
        </>,
        document.body
      )}
    </>
  );
};

const ParceirosModal: React.FC<ParceirosModalProps> = ({ isOpen, onClose }) => {
  const { profile } = useAuth();
  const [parceiros, setParceiros] = useState<Parceiro[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('todos');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [selectedParceiro, setSelectedParceiro] = useState<Parceiro | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    fetchParceiros();
  }, [isOpen]);

  const fetchParceiros = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('parceiros')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setParceiros(data as Parceiro[]);
    }
    setLoading(false);
  };

  const updateStatus = async (id: string, newStatus: string) => {
    setUpdatingId(id);
    setParceiros(prev => prev.map(p => p.id === id ? { ...p, status: newStatus as Parceiro['status'] } : p));

    const { error } = await supabase
      .from('parceiros')
      .update({ status: newStatus })
      .eq('id', id);

    if (error) {
      console.error('Error updating parceiro status:', error);
      fetchParceiros();
    } else {
      logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'mudar_status_parceiro', origem: 'acao' });
    }
    setUpdatingId(null);
  };

  const filtered = useMemo(() => {
    let result = parceiros;

    if (statusFilter !== 'todos') {
      result = result.filter(p => p.status === statusFilter);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(p =>
        p.nome.toLowerCase().includes(term) ||
        p.cnpj.includes(term) ||
        p.cidade.toLowerCase().includes(term) ||
        p.whatsapp.includes(term)
      );
    }

    return result;
  }, [parceiros, searchTerm, statusFilter]);

  const stats = useMemo(() => ({
    total: parceiros.length,
    novos: parceiros.filter(p => p.status === 'novo').length,
    emAnalise: parceiros.filter(p => p.status === 'em_analise').length,
    aprovados: parceiros.filter(p => p.status === 'aprovado').length,
    cidades: new Set(parceiros.map(p => p.cidade)).size,
  }), [parceiros]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCNPJ = (cnpj: string) => {
    const cleaned = cnpj.replace(/\D/g, '');
    if (cleaned.length === 14) {
      return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }
    return cnpj;
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-3 md:p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      {/* Modal Container */}
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl w-full max-w-[95%] md:max-w-[85%] shadow-2xl flex flex-col max-h-[calc(100vh-48px)] animate-in zoom-in-95 slide-in-from-top-4 duration-300">

        {/* Header — Brand green gradient */}
        <div className="flex items-center justify-between px-6 py-8 md:px-10 md:py-10 border-b border-gray-100 dark:border-gray-800 bg-gradient-to-br from-[#5F6C37] via-[#6B7A3E] to-[#4A5429] rounded-t-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 opacity-10 -translate-y-12 translate-x-12">
            <Handshake size={256} strokeWidth={0.5} />
          </div>
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 w-full">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm text-white/90 text-xs font-medium px-3 py-1 rounded-full border border-white/10">
                  <Briefcase size={12} />
                  Gerenciamento
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white font-poppins tracking-tight">
                Interessados em Parceria
              </h1>
              <p className="text-white/80 text-sm mt-1">
                Pessoas que se cadastraram para se tornar parceiros
              </p>
            </div>

            {/* Summary Stats */}
            <div className="flex flex-wrap gap-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/10 min-w-[100px] text-center">
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-[10px] text-white/60 font-semibold uppercase tracking-wider">Total</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/10 min-w-[100px] text-center">
                <p className="text-2xl font-bold text-blue-300">{stats.novos}</p>
                <p className="text-[10px] text-white/60 font-semibold uppercase tracking-wider">Novos</p>
              </div>
              <div className="hidden sm:block bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/10 min-w-[100px] text-center">
                <p className="text-2xl font-bold text-amber-300">{stats.emAnalise}</p>
                <p className="text-[10px] text-white/60 font-semibold uppercase tracking-wider">Em Análise</p>
              </div>
              <div className="hidden sm:block bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/10 min-w-[100px] text-center">
                <p className="text-2xl font-bold text-emerald-300">{stats.aprovados}</p>
                <p className="text-[10px] text-white/60 font-semibold uppercase tracking-wider">Aprovados</p>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="absolute top-5 right-5 p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors z-20"
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 flex flex-col p-4 md:p-6 gap-4 min-h-0">

          {/* Filters Bar */}
          <div className="relative flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center">

            {/* Status Filter — Pill dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className={`flex items-center gap-2.5 pl-3.5 pr-3 py-2 bg-white dark:bg-gray-800 border rounded-full shadow-sm hover:shadow-md transition-all duration-200 group w-full sm:w-auto justify-between sm:justify-start
                  ${showStatusDropdown
                    ? 'border-[#5F6C37] ring-2 ring-[#5F6C37]/20 dark:ring-[#5F6C37]/30'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }
                `}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#5F6C37]/20 to-[#5F6C37]/5 dark:from-[#5F6C37]/30 dark:to-[#5F6C37]/10 flex items-center justify-center flex-shrink-0">
                    <Filter size={12} className="text-[#5F6C37]" />
                  </div>
                  <div className="text-left min-w-0">
                    <p className="text-[9px] text-gray-400 dark:text-gray-500 font-medium uppercase tracking-wider leading-none mb-0.5">Status</p>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate max-w-[200px]">
                      {statusFilter === 'todos' ? 'Todos' : STATUS_CONFIG[statusFilter]?.label || statusFilter}
                    </p>
                  </div>
                </div>
                <ChevronDown
                  size={14}
                  className={`text-gray-400 dark:text-gray-500 transition-transform duration-300 flex-shrink-0 ml-2 ${showStatusDropdown ? 'rotate-180' : ''}`}
                />
              </button>

              {showStatusDropdown && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowStatusDropdown(false)} />
                  <div className="absolute top-full left-0 mt-2 w-full sm:w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 p-1.5 z-50 animate-fade-in">
                    <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 py-1.5">
                      Filtrar por status
                    </p>
                    {[
                      { value: 'todos', label: 'Todos', dot: 'bg-gray-400' },
                      { value: 'novo', label: 'Novos', dot: 'bg-blue-500' },
                      { value: 'em_analise', label: 'Em Análise', dot: 'bg-amber-500' },
                      { value: 'aprovado', label: 'Aprovados', dot: 'bg-emerald-500' },
                      { value: 'rejeitado', label: 'Rejeitados', dot: 'bg-red-500' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setStatusFilter(opt.value);
                          setShowStatusDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all duration-150 flex items-center justify-between mb-0.5 last:mb-0
                          ${statusFilter === opt.value
                            ? 'bg-[#5F6C37]/10 dark:bg-[#5F6C37]/15 text-[#5F6C37] font-bold'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 font-medium'
                          }
                        `}
                      >
                        <span className="flex items-center gap-2.5">
                          <span className={`w-2.5 h-2.5 rounded-full ${opt.dot} flex-shrink-0`} />
                          {opt.label}
                        </span>
                        {statusFilter === opt.value && (
                          <div className="w-2 h-2 rounded-full bg-[#5F6C37] flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Search Input */}
            <div className={`relative flex-1 min-w-0 transition-all duration-300 ${isSearchFocused ? 'sm:max-w-md' : 'sm:max-w-xs'}`}>
              <div className={`absolute left-3 top-1/2 -translate-y-1/2 transition-colors duration-200 ${isSearchFocused ? 'text-[#5F6C37]' : 'text-gray-400 dark:text-gray-500'}`}>
                <Search size={15} />
              </div>
              <input
                type="text"
                placeholder="Buscar por nome, CNPJ, cidade..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                className={`w-full bg-white dark:bg-gray-800 border rounded-full pl-10 pr-9 py-2 text-sm font-medium text-gray-800 dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none transition-all duration-200 shadow-sm
                  ${isSearchFocused
                    ? 'border-[#5F6C37] ring-2 ring-[#5F6C37]/20 dark:ring-[#5F6C37]/30 shadow-md'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md'
                  }
                `}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-150"
                >
                  <X size={13} />
                </button>
              )}
            </div>

            {/* Result count */}
            {!loading && (searchTerm || statusFilter !== 'todos') && (
              <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-700 whitespace-nowrap">
                {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Content — scrollable area */}
          <div className="flex-1 overflow-y-auto custom-scrollbar min-h-0">
            {loading ? (
              <LoadingSpinner label="Carregando interessados..." />
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <Users size={40} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-gray-500 dark:text-gray-400 text-base font-medium">
                  Nenhum interessado encontrado
                </p>
                <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
                  {searchTerm || statusFilter !== 'todos' ? 'Tente ajustar os filtros.' : 'Os cadastros aparecerão aqui.'}
                </p>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
              {/* Table Header — Desktop */}
              <div className="hidden md:grid md:grid-cols-[minmax(200px,1.5fr)_140px_160px_120px_minmax(150px,1.2fr)_120px_130px_120px] gap-4 px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/50">
                <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Nome</span>
                <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">WhatsApp</span>
                <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">CNPJ</span>
                <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Modelo</span>
                <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Cidade</span>
                <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Data</span>
                <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider text-right">Status</span>
                <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider text-center">Ação</span>
              </div>

                <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
                  {filtered.map((parceiro) => {
                    const isUpdating = updatingId === parceiro.id;

                    return (
                      <div
                        key={parceiro.id}
                        className="group hover:bg-gray-50/80 dark:hover:bg-gray-700/30 transition-colors"
                      >
                        {/* Desktop Row */}
                        <div className="hidden md:grid md:grid-cols-[minmax(200px,1.5fr)_140px_160px_120px_minmax(150px,1.2fr)_120px_130px_120px] gap-4 px-4 py-4 items-center">
                          {/* Nome */}
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#5F6C37]/20 to-[#7A8B4A]/10 dark:from-[#5F6C37]/30 dark:to-[#7A8B4A]/20 flex items-center justify-center flex-shrink-0">
                              <span className="text-[#5F6C37] font-bold text-xs">
                                {parceiro.nome.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm py-1" title={parceiro.nome}>
                              {parceiro.nome}
                            </span>
                          </div>

                          {/* WhatsApp */}
                          <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap flex items-center gap-1.5">
                            <Phone size={11} className="text-gray-400 flex-shrink-0" />
                            {parceiro.whatsapp}
                          </span>

                          {/* CNPJ */}
                          <span className="text-gray-600 dark:text-gray-400 font-mono text-xs whitespace-nowrap flex items-center gap-1.5">
                            <Building2 size={11} className="text-gray-400 flex-shrink-0" />
                            {formatCNPJ(parceiro.cnpj)}
                          </span>

                          {/* Modelo */}
                          <span className="text-sm text-gray-600 dark:text-gray-400 capitalize py-1">
                            {parceiro.modelo_atuacao}
                          </span>

                          {/* Cidade */}
                          <div className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-1 py-1" title={parceiro.cidade}>
                            <MapPin size={11} className="text-gray-400 flex-shrink-0 mt-1" />
                            <span>{parceiro.cidade}</span>
                          </div>

                          {/* Data */}
                          <div className="flex flex-col">
                            <span className="text-[11px] text-gray-600 dark:text-gray-300 font-medium whitespace-nowrap flex items-center gap-1">
                              <Calendar size={10} className="flex-shrink-0 text-gray-400" />
                              {new Date(parceiro.created_at).toLocaleDateString('pt-BR')}
                            </span>
                            <span className="text-[9px] text-gray-400 dark:text-gray-500 ml-3.5">
                              {new Date(parceiro.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>

                          {/* Status — Custom Pill */}
                          <div className="flex justify-end pr-2">
                            <StatusPill
                              status={parceiro.status}
                              onChangeStatus={(s) => updateStatus(parceiro.id, s)}
                              disabled={isUpdating}
                            />
                          </div>

                          {/* Ação */}
                          <div className="flex justify-center">
                            {parceiro.status === 'aprovado' && (
                              <button
                                onClick={() => {
                                  setSelectedParceiro(parceiro);
                                  setIsCreateUserModalOpen(true);
                                }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#5F6C37] text-white text-[10px] font-bold rounded-lg hover:bg-[#5F6C37]/90 transition-all shadow-sm hover:shadow-md whitespace-nowrap"
                              >
                                <UserPlus size={12} />
                                Criar acesso
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Mobile Card */}
                        <div className="md:hidden px-4 py-3 space-y-2.5">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2.5 min-w-0 flex-1">
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#5F6C37]/20 to-[#7A8B4A]/10 flex items-center justify-center flex-shrink-0">
                                <span className="text-[#5F6C37] font-bold text-sm">
                                  {parceiro.nome.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate">{parceiro.nome}</p>
                                <p className="text-[11px] text-gray-400 dark:text-gray-500">{formatDate(parceiro.created_at)}</p>
                              </div>
                            </div>
                            <StatusPill
                              status={parceiro.status}
                              onChangeStatus={(s) => updateStatus(parceiro.id, s)}
                              disabled={isUpdating}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 dark:text-gray-400">
                            <span className="flex items-center gap-1"><Phone size={10} /> {parceiro.whatsapp}</span>
                            <span className="flex items-center gap-1"><MapPin size={10} /> {parceiro.cidade}</span>
                            <span className="flex items-center gap-1 col-span-2"><Building2 size={10} /> {formatCNPJ(parceiro.cnpj)}</span>
                            <span className="flex items-center gap-1 col-span-2"><Briefcase size={10} /> {parceiro.modelo_atuacao}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create User Modal Integration */}
      <CreateUserModal
        isOpen={isCreateUserModalOpen}
        onClose={() => {
          setIsCreateUserModalOpen(false);
          setSelectedParceiro(null);
        }}
        initialData={{
          fullName: selectedParceiro?.nome || '',
          whatsapp: selectedParceiro?.whatsapp || '',
          departamentos: ['Parcerias'],
          role: 'parceiro'
        }}
      />
    </div>,
    document.body
  );
};

export default ParceirosModal;
